import { STATE, ROUTE, CODE_VERIFIER, LOGOUT_BROADCAST_KEY } from 'src/constants';
import { USER_SESSION } from 'src/constants';

// we used to use appTimestamp as store key to invalidate auth data with every new version 
const authKey = window.btoa(`${USER_SESSION}`);

export const cleanupOutDatedAuthData = () => {
  if (localStorage.getItem(authKey) === null) {
    localStorage.clear();
  }
};

export const storeInfoBeforeRedirect = ({ state, route = '', codeVerifier = '' }) => {
  localStorage.setItem(STATE, state);
  localStorage.setItem(ROUTE, route);
  if (codeVerifier) {
    localStorage.setItem(CODE_VERIFIER, codeVerifier);
  }
};

export const cleanupAfterComingBack = () => {
  localStorage.removeItem(STATE);
  localStorage.removeItem(CODE_VERIFIER);
};

export const getCodeVerifier = () => localStorage.getItem(CODE_VERIFIER);

export const getRoute = () => localStorage.getItem(ROUTE);

export const setRoute = (route: string) => localStorage.setItem(ROUTE, route);

export const removeRoute = () => localStorage.removeItem(ROUTE);

export const setAuth = data => {
  try {
    const str = JSON.stringify(data);
    localStorage.setItem(authKey, window.btoa(str));
    return true;
  } catch {
    return false;
  }
};

export const getAuth = () => {
  try {
    const str = localStorage.getItem(authKey) || '';
    return JSON.parse(window.atob(str));
  } catch {
    return null;
  }
};

export const removeAuth = () => {
  localStorage.setItem(authKey, window.atob(''));
};

/**
 * Notify other tabs that logout occurred so they clear MSAL cache and reload.
 * Call before redirecting to B2C logout; other tabs listen for storage event.
 */
export const broadcastLogout = () => {
  try {
    localStorage.setItem(LOGOUT_BROADCAST_KEY, Date.now().toString());
  } catch {
    // ignore
  }
};

/** Remove logout broadcast key (e.g. after handling return from B2C logout). */
export const clearLogoutBroadcast = () => {
  try {
    localStorage.removeItem(LOGOUT_BROADCAST_KEY);
  } catch {
    // ignore
  }
};

const FILTERS_CACHE_PREFIX = 'filtersCache.';
// Older than this, a cached result is ignored and the app waits for the
// network as it would on a first visit.
export const FILTERS_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Save the latest getAllFilters result for a site, for the next app load. */
export const setCachedFilters = (siteId: string, data: unknown): void => {
  try {
    localStorage.setItem(
      `${FILTERS_CACHE_PREFIX}${siteId}`,
      JSON.stringify({ savedAt: Date.now(), data })
    );
  } catch {
    // ignore - storage may be full or blocked (e.g. private browsing)
  }
};

/** The cached getAllFilters result for a site, or null if missing or expired. */
export const getCachedFilters = <T = unknown>(
  siteId: string,
  maxAgeMs = FILTERS_CACHE_MAX_AGE_MS
): T | null => {
  try {
    const raw = localStorage.getItem(`${FILTERS_CACHE_PREFIX}${siteId}`);
    if (!raw) {
      return null;
    }
    const { savedAt, data } = JSON.parse(raw) as { savedAt?: unknown; data?: T };
    if (typeof savedAt !== 'number' || Date.now() - savedAt > maxAgeMs || !data) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
};
